import React, { useEffect, useRef, useState } from 'react'
import {
  Avatar as MuiAvatar,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core'
import { FileUploadOutlined, DeleteOutlined, InfoRounded } from '@material-ui/icons'
import { Box, Fade } from '@mui/material'
import Tooltip from '@material-ui/core/Tooltip'
import { toast } from 'utils/notify'
import { colors } from 'utils/colors'
import ImageCropModal from './ImageCropModal'

const ImageSelector = (props) => {
  const {
    title,
    subTitle,
    onChange,
    onRemove,
    initialImage,
    infoText,
    imageProps = {},
    cropOnSelect = false
  } = props

  const classes = useStyles()
  const fileInputRef = useRef()
  const [imageUrl, setImageUrl] = useState(null)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [originalFileName, setOriginalFileName] = useState('')

  useEffect(() => {
    if (initialImage) {
      setImageUrl(initialImage)
    }

    return () => {
      // Clean up object URLs to avoid memory leaks
      if (imageUrl && !initialImage) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [initialImage])

  const handleImageChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    const allowedFileTypes = ['image/jpg', 'image/jpeg', 'image/png']
    if (!allowedFileTypes.includes(file.type)) {
      toast.warning('Only JPG and PNG images are supported.')
      return
    }

    // Store original file info for cropping
    setOriginalFileName(file.name)

    if (cropOnSelect) {
      // Open crop modal instead of immediate processing
      const objectUrl = URL.createObjectURL(file)
      setImageUrl(objectUrl)
      setCropModalOpen(true)
    } else {
      // Original behavior - no cropping
      // Clean up previous object URL if it exists
      if (imageUrl && !initialImage) {
        URL.revokeObjectURL(imageUrl)
      }

      // Create a new object URL for preview
      const objectUrl = URL.createObjectURL(file)
      setImageUrl(objectUrl)

      // Call the onChange callback with the selected file
      if (onChange) {
        onChange(file)
      }
    }
    // Reset the file input
    event.target.value = null
  }

  const handleRemove = () => {
    if (imageUrl && !initialImage) {
      URL.revokeObjectURL(imageUrl)
    }
    setImageUrl(null)

    // Call the onRemove callback
    if (onRemove) {
      onRemove()
    }
  }

  const handleButtonClick = () => {
    if (imageUrl) {
      handleRemove()
    } else {
      fileInputRef.current.click()
    }
  }

  const handleCropComplete = (croppedFile) => {
    try {
      // Close modal first
      setCropModalOpen(false)
      setOriginalFileName('')

      // Clean up previous object URL if it exists
      if (imageUrl && !initialImage) {
        URL.revokeObjectURL(imageUrl)
      }

      // Update image preview with cropped file
      const objectUrl = URL.createObjectURL(croppedFile)
      setImageUrl(objectUrl)

      // Call the onChange callback with the cropped file
      if (onChange) {
        onChange(croppedFile)
      }
    } catch (error) {
      console.error('Error handling cropped image:', error)
      toast.error('Image processing failed. Please try again.')
    }
  }

  const handleCropCancel = () => {
    setCropModalOpen(false)
    setOriginalFileName('')

    // Clean up the temporary image URL
    if (imageUrl && !initialImage) {
      URL.revokeObjectURL(imageUrl)
    }

    // Reset to initial state
    if (initialImage) {
      setImageUrl(initialImage)
    } else {
      setImageUrl(null)
    }
  }

  return (
    <>
      <Box className={classes.heading}>
        <Typography variant="h6" color="secondary">
          {title}
        </Typography>
        {infoText && (
          <Tooltip
            arrow
            TransitionComponent={Fade}
            title={<Typography className={classes.tooltip}>{infoText}</Typography>}>
            <IconButton className={classes.infoIconBtn}>
              <InfoRounded className={classes.infoIcon} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" color="textSecondary" className={classes.subTitle}>
        {subTitle}
      </Typography>

      <Card variant="elevation" className={classes.imageCard}>
        <CardContent className={classes.cardContent}>
          {imageUrl ? (
            <MuiAvatar
              variant="square"
              imgProps={imageProps}
              className={classes.image}
              src={imageUrl}
            />
          ) : (
            <Box className={classes.emptyImageBox}>
              <FileUploadOutlined className={classes.uploadIcon} />
              <Typography variant="body2" color="textSecondary">
                Select Image
              </Typography>
            </Box>
          )}
        </CardContent>
        <Divider className={classes.divider} />
        <Box className={classes.actionBox}>
          <input
            hidden
            accept="image/*"
            type="file"
            onChange={handleImageChange}
            ref={fileInputRef}
          />
          <Button
            color="primary"
            variant={imageUrl ? 'outlined' : 'contained'}
            className={imageUrl ? classes.removeButton : classes.selectButton}
            startIcon={imageUrl ? <DeleteOutlined /> : <FileUploadOutlined />}
            onClick={handleButtonClick}>
            {imageUrl ? 'Remove' : 'Select Image'}
          </Button>

          {imageUrl && (
            <Button
              color="primary"
              variant="contained"
              className={classes.changeButton}
              startIcon={<FileUploadOutlined />}
              onClick={() => fileInputRef.current.click()}>
              Change
            </Button>
          )}
        </Box>
      </Card>

      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        onClose={handleCropCancel}
        imageSrc={imageUrl}
        fileName={originalFileName}
        onCropComplete={handleCropComplete}
        title={`Crop ${title}`}
      />
    </>
  )
}

export default ImageSelector

const useStyles = makeStyles({
  heading: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '15px'
  },
  tooltip: {
    padding: '5px',
    fontSize: '13px'
  },
  infoIcon: {
    color: colors.indigo,
    fontSize: '18px'
  },
  infoIconBtn: {
    padding: '2px'
  },
  imageCard: {
    borderRadius: '12px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)'
    }
  },
  emptyImageBox: {
    height: '180px',
    width: '180px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #e0e0e0',
    borderRadius: '8px',
    margin: '16px',
    backgroundColor: '#f9f9f9'
  },
  uploadIcon: {
    fontSize: '40px',
    color: '#bdbdbd',
    marginBottom: '8px'
  },
  image: {
    height: '180px',
    width: '180px',
    margin: '16px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 16px'
  },
  divider: {
    height: '1px',
    backgroundColor: '#f0f0f0'
  },
  actionBox: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: '16px'
  },
  selectButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: '0 3px 10px rgba(21, 180, 219, 0.15)'
  },
  removeButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500
    // borderColor: '#ff7851'
  },
  changeButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: '0 3px 10px rgba(21, 180, 219, 0.15)'
  },
  subTitle: {
    marginBottom: 12,
    fontSize: '13px',
    fontStyle: 'italic',
    color: '#757575'
  }
})
